import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo } from 'react';
import { SeasonFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import SelectField from '@amf/shared/components/form/SelectField';
import { findTeamDefaults } from '@amf/shared/data/Team';

const schema = yup.object({
  season: yup.string(),
  league: yup.string(),
});

export interface TeamRosterFilterData {
  season: string;
  league: string;
}

interface TeamRosterFilterProps {
  team: TeamListItemFragment;
  seasons: SeasonFragment[];
  onUpdate: (data: TeamRosterFilterData) => Promise<void>;
}

function TeamRosterFilter({ team, onUpdate, seasons }: TeamRosterFilterProps) {
  const { t } = useTranslate();
  const { league: defaultLeague, season: defaultSeason } = findTeamDefaults(team, seasons);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TeamRosterFilterData>({
    resolver: yupResolver(schema),
    defaultValues: {
      season: defaultSeason?.id ?? seasons[0].id,
      league: defaultLeague?.id ?? seasons[0].leagues[0].id,
    },
  });

  const update = useCallback(
    async (data: TeamRosterFilterData) => {
      await onUpdate(data);
    },
    [onUpdate],
  );

  watch(update);

  const currentSelectedSeasonId = watch('season');
  const season = useMemo(() => {
    const currentSeason = seasons.find(s => s.id === currentSelectedSeasonId) ?? null;
    if (currentSeason) setValue('league', currentSeason.leagues[0].id);

    return currentSeason;
  }, [currentSelectedSeasonId, seasons, setValue]);

  if (!season) return null;
  return (
    <form onSubmit={handleSubmit(onUpdate)}>
      <Controller
        name='season'
        control={control}
        render={({ field }) => (
          <SelectField
            value={field.value}
            label={t('team.roster.season')}
            error={errors.season?.message}
            {...field}
          >
            {seasons.map(season => (
              <option key={season.id} value={season.id}>
                {season.name}
              </option>
            ))}
          </SelectField>
        )}
      />
      <Controller
        name='league'
        control={control}
        render={({ field }) => (
          <SelectField
            value={field.value}
            label={t('team.roster.league')}
            error={errors.league?.message}
            {...field}
          >
            {season.leagues.map(league => (
              <option key={league.id} value={league.id}>
                {league.name}
              </option>
            ))}
          </SelectField>
        )}
      />
    </form>
  );
}

export default TeamRosterFilter;
